import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permission } from '@iecn/shared';
import { useAuth } from './lib/auth';
import { Spinner } from './ui';

const Home = lazy(() => import('./routes/Home'));
const Pricing = lazy(() => import('./routes/Pricing'));
const Book = lazy(() => import('./routes/Book'));
const Track = lazy(() => import('./routes/Track'));
const SignIn = lazy(() => import('./routes/SignIn'));
const ForgotPassword = lazy(() => import('./routes/ForgotPassword'));
const ResetPassword = lazy(() => import('./routes/ResetPassword'));
const Account = lazy(() => import('./routes/Account'));
const DriverApp = lazy(() => import('./routes/driver/DriverApp'));
const Dispatch = lazy(() => import('./routes/command/Dispatch'));
const Executive = lazy(() => import('./routes/command/Executive'));

function LoadingScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner className="h-8 w-8 text-navy-700" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

/**
 * Client-side gate. This decides what to *render* — it is not the security boundary.
 * Every API route re-checks the caller's role on the server.
 */
function Protected({
  children,
  permission,
  driverOnly,
}: {
  children: React.ReactNode;
  permission?: Permission;
  driverOnly?: boolean;
}) {
  const { user, loading, can } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  if (driverOnly && !user.driver) return <Navigate to={user.home} replace />;
  if (permission && !can(permission)) return <Navigate to={user.home} replace />;

  return <>{children}</>;
}

export default function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/book" element={<Book />} />
        <Route path="/track" element={<Track />} />
        <Route path="/track/:token" element={<Track />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/account"
          element={
            <Protected>
              <Account />
            </Protected>
          }
        />

        <Route
          path="/driver/*"
          element={
            <Protected driverOnly>
              <DriverApp />
            </Protected>
          }
        />
        <Route
          path="/command/dispatch"
          element={
            <Protected permission="dispatch:view">
              <Dispatch />
            </Protected>
          }
        />
        <Route
          path="/command/executive"
          element={
            <Protected permission="report:operations">
              <Executive />
            </Protected>
          }
        />

        <Route path="/portal" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
